import { Box, Breadcrumb, Heading } from 'lucid-components-react';
import React, { useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';

import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm
} from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';
import { mqMin } from 'theme/base';
import { Text } from 'theme/typography';
import { LucidTheme } from 'types/types';

interface Props {
  header: string | string[];
  subheader?: string;
  backgroundImage?: string;
  hasBackgroundImage?: boolean;
  smallBgImage?: boolean;
  breadCrumbHub?: string;
  breadCrumbPage?: string;
  fullSunrise?: boolean;
  pageType?: string;
  videoLink?: string;
  buttons?: Drupal_ParagraphCtaButton[];
}

const Hero = ({
  header,
  subheader,
  backgroundImage,
  hasBackgroundImage,
  breadCrumbHub,
  breadCrumbPage,
  pageType,
  buttons
}: Props) => {
  const { language } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);
  return (
    <Box as="section" css={{ label: `${pageType}` }} pt={{ default: 7 }}>
      <LucidContainer
        css={{
          label: 'lucidContainer',
          [mqMin.tabletLandscape]: {
            display: 'flex'
          }
        }}
      >
        {breadCrumbHub && (
          <Breadcrumb
            items={[
              <Link
                key={breadCrumbHub}
                secondary
                to={
                  breadCrumbHub !== 'Solutions'
                    ? `/solutions/${breadCrumbHub.toLowerCase()}`
                    : '/solutions'
                }
              >
                {breadCrumbHub}
              </Link>,
              breadCrumbPage
            ]}
          />
        )}
        <Box css={{ label: 'textContent', maxWidth: '36em' }}>
          <Heading
            maxWidth={{ default: '30rem', tablet: '60rem' }}
            mb={{ default: 0, tablet: 8 }}
            variant="headlineM"
          >
            {header && Array.isArray(header)
              ? ReactHtmlParser(header.join('<br/>'))
              : header}
          </Heading>
          <Box pt={{ tabletSmall: 0 }} py={{ default: 6 }}>
            {subheader && <Text textStyles="subhead">{subheader}</Text>}
          </Box>
          {buttons && (
            <Box css={{ label: 'buttons' }} display="flex">
              {buttons.map((button, i) => {
                const entity = button.fieldMarketo
                  ? (button.fieldMarketo as {
                    entity: Drupal_ParagraphMarketoForm;
                  })
                  : null;
                const marketo = entity?.entity || null;
                const { url, copy } = getMarketoLink(marketo?.fieldMarketoId)!;
                return (
                  (marketo?.fieldMarketoId && (
                    <Link
                      key={
                        button.fieldButtonText || `sunrise-header-button-${i}`
                      }
                      asButton
                      css={{
                        flex: '0 1 100%',
                        maxWidth: '100%',
                        [mqMin.tabletSmall]: {
                          flex: '0 1 35%',
                          maxWidth: '35%'
                        }
                      }}
                      primary={i === 0}
                      secondary={i !== 0}
                      to={url}
                    >
                      {button.fieldButtonText || copy}
                    </Link>
                  )) ||
                  (button.fieldButtonLink && (
                    <Link
                      key={
                        button.fieldButtonText || `sunrise-header-button-${i}`
                      }
                      asButton
                      primary={i === 0}
                      secondary={i !== 0}
                      to={button.fieldButtonLink}
                    >
                      {button.fieldButtonText}
                    </Link>
                  ))
                );
              })}
            </Box>
          )}
        </Box>
        {hasBackgroundImage && (
          <Box
            css={(theme: LucidTheme) => ({
              label: 'image',
              display: 'none',
              visibility: 'hidden',
              [mqMin.tabletSmall]: {
                display: 'block',
                visibility: 'visible',
                height: '500px',
                paddingTop: theme.space[6],
                width: 'calc(100% + (3.75rem * 2))',
                position: 'relative',
                left: '-3.75rem'
              },
              [mqMin.tabletLandscape]: {
                height: 'auto',
                left: 'unset',
                width: 'unset',
                paddingTop: 'unset',
                paddingLeft: theme.space[4]
              }
            })}
          >
            <img
              alt="Hero Background"
              css={{
                height: '100%',
                width: '100%',
                objectFit: 'cover'
              }}
              src={backgroundImage}
            />
          </Box>
        )}
      </LucidContainer>
    </Box>
  );
};

export default Hero;
